import { z } from 'zod'

const PAGE_ROWS_STORAGE_KEY = 'gecko_embeddedSidebar_pageRows:v1'
const TOP_LEVEL_ROW_SELECTOR =
  '[data-sidebar="menu"] > [data-sidebar="menu-item"]'

export const DEFAULT_PAGE_ROWS = 6
export const MAX_PAGES_WITHOUT_RESIZE = 5
export const MIN_PAGE_ROWS = 1
export const CHAT_AREA_RESERVED_REM = 10

const storedPageRowsSchema = z.coerce.number().int().min(MIN_PAGE_ROWS)

export function readStoredPageRows(): number {
  try {
    const result = storedPageRowsSchema.safeParse(
      localStorage.getItem(PAGE_ROWS_STORAGE_KEY)
    )
    return result.success ? result.data : DEFAULT_PAGE_ROWS
  } catch {
    return DEFAULT_PAGE_ROWS
  }
}

export function writeStoredPageRows(rows: number): void {
  try {
    localStorage.setItem(PAGE_ROWS_STORAGE_KEY, String(rows))
  } catch {
    /* localStorage unavailable */
  }
}

function clampPageRows(rows: number, maxRows: number): number {
  return Math.max(MIN_PAGE_ROWS, Math.min(rows, maxRows))
}

export function pageRowsForDrag({
  deltaY,
  maxRows,
  rowPitch,
  startRows,
}: {
  deltaY: number
  maxRows: number
  rowPitch: number
  startRows: number
}): number {
  return clampPageRows(startRows + Math.round(deltaY / rowPitch), maxRows)
}

export function pageRowsForKey({
  key,
  maxRows,
  rows,
}: {
  key: string
  maxRows: number
  rows: number
}): number | undefined {
  switch (key) {
    case 'ArrowUp':
      return clampPageRows(rows - 1, maxRows)
    case 'ArrowDown':
      return clampPageRows(rows + 1, maxRows)
    case 'Home':
      return MIN_PAGE_ROWS
    case 'End':
      return clampPageRows(maxRows, maxRows)
    default:
      return undefined
  }
}

export function measurePageRowPitch(
  pagesPane: HTMLElement
): number | undefined {
  const [first, second] = Array.from(
    pagesPane.querySelectorAll(TOP_LEVEL_ROW_SELECTOR)
  )
  if (!first) return undefined
  const firstRect = first.getBoundingClientRect()
  const pitch = second
    ? second.getBoundingClientRect().top - firstRect.top
    : firstRect.height +
      (first.parentElement
        ? parseFloat(getComputedStyle(first.parentElement).rowGap) || 0
        : 0)
  return pitch > 0 ? pitch : undefined
}

export function maxPageRowsThatFit({
  pagesPane,
  rowPitch,
}: {
  pagesPane: HTMLElement
  rowPitch: number
}): number {
  const renderedRows = pagesPane.querySelectorAll(TOP_LEVEL_ROW_SELECTOR).length
  const container = pagesPane.parentElement
  if (!container) return Math.max(MIN_PAGE_ROWS, renderedRows)
  const rootFontSizePx = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  )
  const spareHeight =
    container.getBoundingClientRect().bottom -
    pagesPane.getBoundingClientRect().top -
    CHAT_AREA_RESERVED_REM * rootFontSizePx -
    pagesPane.getBoundingClientRect().height
  return Math.max(
    MIN_PAGE_ROWS,
    renderedRows + Math.floor(spareHeight / rowPitch)
  )
}
